import type { TableColumn } from "@shared/types/query";
import { z } from "zod";
import type { ValidationMessages } from "./hooks/useValidationMessages";

export type FieldDef = {
  column_name: string;
  type: "text" | "email" | "password" | "textarea" | "number";
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    patternMessage?: string;
  };
  required?: boolean;
};

export function buildZodField(
  field: FieldDef,
  msg: ValidationMessages
): z.ZodTypeAny {
  let schema: z.ZodTypeAny;

  switch (field.type) {
    case "text":
    case "email":
    case "password":
    case "textarea": {
      let s = z.string();
      if (field.type === "email") s = s.email(msg.email);
      if (field.validation?.min)
        s = s.min(field.validation.min, msg.stringMin(field.validation.min));
      if (field.validation?.max)
        s = s.max(field.validation.max, msg.stringMax(field.validation.max));
      if (field.validation?.pattern)
        s = s.regex(
          new RegExp(field.validation.pattern),
          msg.pattern(field.validation.patternMessage)
        );
      return field.required ? s.min(1, msg.required) : s.optional();
    }
    case "number": {
      let s = z.coerce.number();
      if (field.validation?.min !== undefined)
        s = s.min(field.validation.min, msg.numberMin(field.validation.min));
      if (field.validation?.max !== undefined)
        s = s.max(field.validation.max, msg.numberMax(field.validation.max));
      return field.required ? s : s.optional();
    }

    default:
      schema = z.unknown();
  }

  return schema;
}

const EXCLUDED_FIELDS = new Set(["id", "created_at", "updated_at", "uuid"]);

export function buildFormFields(columns: TableColumn[]) {
  return columns.filter((column) => !EXCLUDED_FIELDS.has(column.column_name));
}
